<template>
  <div class="dashboard-page">
    <v-container fluid class="dashboard-container">
      <section class="dashboard-hero">
        <div>
          <div class="dashboard-eyebrow">Dashboard</div>
          <h1 class="dashboard-title">Welcome back, {{ userName }}</h1>
          <p class="dashboard-subtitle">
            Here is what needs your attention at {{ businessName }} today.
          </p>
        </div>
        <div class="dashboard-actions">
          <span v-if="lastUpdated" class="last-updated">
            Updated {{ lastUpdated | moment("from", "now") }}
          </span>
          <v-btn outlined color="primary" :loading="isLoading" @click="loadDashboard">
            <v-icon left size="18">mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>
      </section>

      <v-alert v-if="loadError" type="warning" text class="mb-6">
        Some dashboard information could not be loaded. Refresh to try again.
      </v-alert>

      <section aria-labelledby="overview-heading">
        <div class="section-heading">
          <div>
            <h2 id="overview-heading">Overview</h2>
            <p>A quick view of your school community and balances</p>
          </div>
        </div>
        <v-row>
          <v-col v-for="item in overviewCards" :key="item.label" cols="12" sm="6" lg="4">
            <v-card class="metric-card" elevation="0" role="link" tabindex="0" @click="navigate(item.route)" @keydown.enter="navigate(item.route)">
              <v-card-text>
                <div class="metric-topline">
                  <div class="metric-icon">
                    <v-icon color="primary" size="23">{{ item.icon }}</v-icon>
                  </div>
                  <v-icon color="#94A3B8" size="20">mdi-arrow-top-right</v-icon>
                </div>
                <v-skeleton-loader v-if="isLoading && !hasLoaded" type="text" width="110" />
                <div v-else class="metric-value">{{ formatMetric(item) }}</div>
                <div class="metric-label">{{ item.label }}</div>
                <div class="metric-helper">{{ item.helper }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <section class="teacher-performance-section" aria-labelledby="teacher-performance-heading">
        <div class="section-heading">
          <div>
            <h2 id="teacher-performance-heading">Attendance-taking performance</h2>
            <p>How consistently teachers completed classroom attendance in the last 30 days</p>
          </div>
          <v-btn text color="primary" class="performance-view-all" @click="navigate({ name: 'teacher-list' })">
            View teachers <v-icon right size="18">mdi-arrow-right</v-icon>
          </v-btn>
        </div>

        <v-card class="teacher-performance-card" elevation="0">
          <v-skeleton-loader v-if="isLoading && !hasLoaded" type="article, list-item-two-line" />
          <template v-else>
            <div class="performance-summary">
              <div class="performance-score" :class="performanceScoreClass">
                <strong>{{ teacherPerformance.schoolAverage || 0 }}%</strong>
                <span>School score</span>
              </div>
              <div class="performance-summary-copy">
                <span class="performance-kicker">Last 30 days</span>
                <h3>{{ performanceHeadline }}</h3>
                <p>Based on attendance frequency and completed student rosters.</p>
              </div>
              <div class="performance-metrics">
                <div><strong>{{ teacherPerformance.participatingTeachers || 0 }}/{{ teacherPerformance.teacherCount || 0 }}</strong><span>Teachers participating</span></div>
                <div class="performance-metric-alert"><strong>{{ teacherPerformance.needsAttentionCount || 0 }}</strong><span>Need follow-up</span></div>
              </div>
            </div>

            <div class="performance-lists">
              <div class="performance-list">
                <div class="performance-list-heading"><span><v-icon size="17">mdi-trophy-outline</v-icon>Leading teachers</span><small>Consistency leaders</small></div>
                <button v-for="teacher in teacherPerformance.topTeachers" :key="`top-${teacher.teacherId}`" type="button" @click="navigate({ name: 'teacher-list' })">
                  <span class="performance-avatar">{{ initials(teacher.name) }}</span>
                  <span class="performance-teacher"><strong>{{ teacher.name }}</strong><small>{{ teacher.sessions }} attendance sessions</small></span>
                  <span class="performance-teacher-score performance-teacher-score--good">{{ teacher.score }}%</span>
                </button>
                <div v-if="!teacherPerformance.topTeachers.length" class="performance-empty">No attendance activity recorded yet.</div>
              </div>

              <div class="performance-list">
                <div class="performance-list-heading"><span><v-icon size="17">mdi-account-alert-outline</v-icon>Needs follow-up</span><small>Below 60%</small></div>
                <button v-for="teacher in teacherPerformance.needsAttentionTeachers" :key="`attention-${teacher.teacherId}`" type="button" @click="navigate({ name: 'teacher-list' })">
                  <span class="performance-avatar performance-avatar--alert">{{ initials(teacher.name) }}</span>
                  <span class="performance-teacher"><strong>{{ teacher.name }}</strong><small>{{ teacher.sessions ? `${teacher.sessions} attendance sessions` : 'No attendance submitted' }}</small></span>
                  <span class="performance-teacher-score">{{ teacher.score }}%</span>
                </button>
                <div v-if="!teacherPerformance.needsAttentionTeachers.length" class="performance-empty performance-empty--success">Everyone is at or above the follow-up threshold.</div>
              </div>
            </div>
          </template>
        </v-card>
      </section>

      <section class="attention-section" aria-labelledby="attention-heading">
        <div class="section-heading">
          <div>
            <h2 id="attention-heading">Needs attention</h2>
            <p>Items that may be blocking billing or follow-up</p>
          </div>
          <v-chip small color="primary" text-color="primary" outlined>
            {{ totalAttentionItems }} open items
          </v-chip>
        </div>
        <v-row>
          <v-col v-for="item in attentionCards" :key="item.label" cols="12" md="6">
            <v-card class="attention-card" elevation="0" role="link" tabindex="0" @click="navigate(item.route)" @keydown.enter="navigate(item.route)">
              <div class="attention-marker"></div>
              <v-card-text class="d-flex align-center">
                <div class="attention-icon">
                  <v-icon color="primary">{{ item.icon }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="attention-label">{{ item.label }}</div>
                  <div class="attention-helper">{{ item.helper }}</div>
                </div>
                <div class="attention-value">{{ item.value }}</div>
                <v-icon class="ml-4" color="#94A3B8">mdi-chevron-right</v-icon>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <section class="work-section" aria-labelledby="work-heading">
        <div class="section-heading">
          <div>
            <h2 id="work-heading">Application follow-up</h2>
            <p>Prioritize the applications waiting for action</p>
          </div>
        </div>
        <v-row align="stretch">
          <v-col cols="12" lg="6">
            <PendingApplication :classes-list="classesList" :total-student="totalStudent" />
          </v-col>
          <v-col cols="12" lg="6">
            <PendingComment :un-comment-leads="unCommentLeads" :un-comment-count="unCommentLeads.length" />
          </v-col>
        </v-row>
      </section>
    </v-container>
  </div>
</template>

<script>
import classService from "@/modules/Classes/service.js";
import leadService from "@/modules/Application/service.js";
import customerService from "@/modules/Customer/service.js";
import donorService from "@/modules/Donors/service.js";
import studentService from "@/modules/Student/service.js";
import PendingApplication from "./components/PendingApplication.vue";
import PendingComment from "./components/PendingComments.vue";
import homeService from "./service.js";
import { mapGetters } from "vuex";

export default {
  name: "home",
  components: { PendingApplication, PendingComment },
  data() {
    return {
      classesList: [],
      unCommentLeads: [],
      studentCount: 0,
      Customer: 0,
      CustomerOutstandingBal: 0,
      DonorOutstandingBal: 0,
      CustomerWithoutSub: 0,
      isLoading: false,
      hasLoaded: false,
      loadError: false,
      lastUpdated: null,
      teacherPerformance: {
        schoolAverage: 0,
        teacherCount: 0,
        participatingTeachers: 0,
        needsAttentionCount: 0,
        topTeachers: [],
        needsAttentionTeachers: [],
      },
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser", "currentBusiness"]),
    userName() {
      return (this.currentUser && this.currentUser.name) || "there";
    },
    businessName() {
      return (this.currentBusiness && this.currentBusiness.name) || "Taleem";
    },
    totalStudent() {
      return this.classesList.reduce((total, row) => total + Number(row.totalStudentLeads || 0), 0);
    },
    totalAttentionItems() {
      return Number(this.Customer || 0) + Number(this.CustomerWithoutSub || 0);
    },
    performanceScoreClass() {
      const score = Number(this.teacherPerformance.schoolAverage || 0);
      if (score >= 75) return "performance-score--good";
      if (score >= 60) return "performance-score--watch";
      return "performance-score--alert";
    },
    performanceHeadline() {
      const score = Number(this.teacherPerformance.schoolAverage || 0);
      if (!this.teacherPerformance.teacherCount) return "No active teachers to measure";
      if (score >= 85) return "Attendance routines are strong";
      if (score >= 60) return "Attendance is moving in the right direction";
      return "Some teachers need attendance follow-up";
    },
    overviewCards() {
      return [
        { label: "Total students", value: this.studentCount, helper: "Active student records", icon: "mdi-school-outline", route: "/students/list" },
        { label: "Outstanding fees", value: this.CustomerOutstandingBal, helper: "Parent balances to collect", icon: "mdi-receipt-text-clock-outline", currency: true, route: { path: "/parents/list", query: { ShowBalanceOnly: true } } },
        { label: "Outstanding donations", value: this.DonorOutstandingBal, helper: "Donor balances to follow up", icon: "mdi-hand-heart-outline", currency: true, route: { path: "/donors/list", query: { ShowBalanceOnly: true } } },
      ];
    },
    attentionCards() {
      return [
        { label: "Invoices not created", value: this.Customer, helper: "Parents missing this month’s invoice", icon: "mdi-file-document-alert-outline", route: "/parents/noInvoice" },
        { label: "Missing subscriptions", value: this.CustomerWithoutSub, helper: "Parents without a payment schedule", icon: "mdi-calendar-alert-outline", route: "/parents/noSubscription" },
      ];
    },
  },
  created() {
    document.title = "Taleem - Dashboard";
    this.loadDashboard();
  },
  methods: {
    navigate(route) {
      if (route) this.$router.push(route);
    },
    formatMetric(item) {
      if (item.currency) {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(item.value || 0));
      }
      return new Intl.NumberFormat("en-US").format(Number(item.value || 0));
    },
    initials(name) {
      return String(name || "Teacher").split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    async loadDashboard() {
      this.isLoading = true;
      this.loadError = false;
      const invoiceParams = {
        start: this.$moment().startOf("month").toISOString(),
        end: this.$moment().endOf("month").toISOString(),
        limit: 1,
      };
      const tasks = [
        studentService.getStudentCount().then(data => { this.studentCount = data.count; }),
        customerService.getAllNoInvoice(invoiceParams).then(data => { this.Customer = data.count; }),
        customerService.getAllNoSubscription().then(data => { this.CustomerWithoutSub = data.count; }),
        customerService.getOutstandingBalance().then(data => { this.CustomerOutstandingBal = data.data; }),
        donorService.getOutstandingBalance().then(data => { this.DonorOutstandingBal = data.data; }),
        classService.getClassInfo().then(data => { this.classesList = data.data || []; }),
        leadService.leadsWithoutComment().then(data => { this.unCommentLeads = data.data || []; }),
        homeService.getTeacherAttendancePerformance({ days: 30 }).then(data => {
          this.teacherPerformance = Object.assign({}, this.teacherPerformance, data.data || {});
        }),
      ];
      const results = await Promise.allSettled(tasks);
      this.loadError = results.some(result => result.status === "rejected");
      this.isLoading = false;
      this.hasLoaded = true;
      this.lastUpdated = new Date();
    },
  },
};
</script>

<style scoped>
.dashboard-page { min-height: 100%; background: #f6f8fb; }
.dashboard-container { padding: 32px 36px 56px; }
.dashboard-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 32px; }
.dashboard-eyebrow { color: #43a047; font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 6px; }
.dashboard-title { color: #172033; font-size: 30px; line-height: 1.2; font-weight: 750; letter-spacing: -.03em; margin: 0; }
.dashboard-subtitle, .section-heading p { color: #64748b; margin: 7px 0 0; }
.dashboard-actions { display: flex; align-items: center; gap: 16px; }
.last-updated { color: #64748b; font-size: 13px; }
.section-heading { display: flex; align-items: center; justify-content: space-between; margin: 0 0 14px; }
.section-heading h2 { color: #253047; font-size: 18px; line-height: 1.3; margin: 0; }
.section-heading p { font-size: 13px; margin-top: 3px; }
.metric-card, .attention-card { border: 1px solid #e5e9e5; border-radius: 12px !important; cursor: pointer; transition: border-color .2s, box-shadow .2s; overflow: hidden; }
.metric-card:hover, .attention-card:hover { border-color: #a5d6a7; box-shadow: 0 6px 18px rgba(46, 125, 50, .08) !important; }
.metric-card .v-card__text { padding: 21px 22px 20px; }
.metric-topline { display: flex; justify-content: space-between; align-items: center; margin-bottom: 19px; }
.metric-icon, .attention-icon { width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; background: #f1f8f2; }
.metric-value { color: #172033; font-size: 30px; font-weight: 750; line-height: 1.15; letter-spacing: -.03em; }
.metric-label { color: #334155; font-size: 14px; font-weight: 650; margin-top: 7px; }
.metric-helper, .attention-helper { color: #8491a5; font-size: 12px; margin-top: 3px; }
.attention-section, .work-section { margin-top: 32px; }
.teacher-performance-section { margin-top: 32px; }
.performance-view-all { font-size: 12px; font-weight: 700; letter-spacing: 0; text-transform: none; }
.teacher-performance-card { overflow: hidden; border: 1px solid #dfe8e1; border-radius: 14px !important; }
.performance-summary { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 22px; padding: 22px 24px; background: linear-gradient(135deg, #f2f9f4, #fbfdfb); }
.performance-score { display: flex; width: 96px; height: 96px; align-items: center; justify-content: center; border: 8px solid #dcefe2; border-radius: 50%; flex-direction: column; background: #fff; }
.performance-score strong { color: #285f3b; font-size: 25px; line-height: 1; letter-spacing: -.04em; }
.performance-score span { margin-top: 5px; color: #718096; font-size: 9px; font-weight: 700; text-transform: uppercase; }
.performance-score--watch { border-color: #f3dfac; }
.performance-score--alert { border-color: #f4c8c5; }
.performance-kicker { color: #2f7d4a; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.performance-summary-copy h3 { margin: 4px 0 3px; color: #172033; font-size: 18px; }
.performance-summary-copy p { margin: 0; color: #718096; font-size: 12px; }
.performance-metrics { display: grid; grid-template-columns: repeat(2, 120px); overflow: hidden; border: 1px solid #dfe8e1; border-radius: 11px; background: #fff; }
.performance-metrics > div { display: flex; min-height: 72px; justify-content: center; padding: 11px 13px; flex-direction: column; }
.performance-metrics > div + div { border-left: 1px solid #e7ece8; }
.performance-metrics strong { color: #172033; font-size: 20px; }
.performance-metrics span { margin-top: 2px; color: #7a8799; font-size: 10px; }
.performance-metric-alert strong { color: #b14d48; }
.performance-lists { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); border-top: 1px solid #e7ece8; }
.performance-list { padding: 17px 20px 19px; }
.performance-list + .performance-list { border-left: 1px solid #e7ece8; }
.performance-list-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
.performance-list-heading > span { display: flex; align-items: center; gap: 7px; color: #334155; font-size: 12px; font-weight: 750; }
.performance-list-heading .v-icon { color: #2f7d4a; }
.performance-list-heading small { color: #94a0b0; font-size: 9px; }
.performance-list button { display: flex; width: 100%; min-height: 54px; align-items: center; gap: 10px; padding: 8px 7px; border: 0; border-top: 1px solid #eef1ef; color: inherit; background: transparent; text-align: left; cursor: pointer; }
.performance-list button:hover { background: #f8fbf9; }
.performance-avatar { display: flex; width: 35px; height: 35px; flex: 0 0 35px; align-items: center; justify-content: center; border-radius: 9px; color: #28633d; background: #e9f5ed; font-size: 10px; font-weight: 800; }
.performance-avatar--alert { color: #9a4b46; background: #fceceb; }
.performance-teacher { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.performance-teacher strong { overflow: hidden; color: #273247; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.performance-teacher small { margin-top: 2px; color: #8491a5; font-size: 9px; }
.performance-teacher-score { color: #b14d48; font-size: 12px; font-weight: 800; }
.performance-teacher-score--good { color: #2f7d4a; }
.performance-empty { padding: 18px 8px; border-top: 1px solid #eef1ef; color: #8491a5; font-size: 11px; text-align: center; }
.performance-empty--success { color: #2f7d4a; }
.attention-card { position: relative; }
.attention-card .v-card__text { min-height: 96px; padding: 18px 20px 18px 25px; }
.attention-marker { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: #66bb6a; }
.attention-icon { margin-right: 15px; flex: 0 0 44px; }
.attention-label { color: #273247; font-size: 14px; font-weight: 650; }
.attention-value { color: #172033; font-size: 27px; font-weight: 750; }
@media (max-width: 700px) {
  .dashboard-container { padding: 22px 16px 40px; }
  .dashboard-hero { align-items: flex-start; flex-direction: column; margin-bottom: 26px; }
  .dashboard-title { font-size: 25px; }
  .dashboard-actions { justify-content: space-between; width: 100%; }
  .attention-card .v-card__text { padding-right: 12px; }
  .attention-helper { max-width: 160px; }
  .teacher-performance-section .section-heading { align-items: flex-start; gap: 8px; }
  .performance-view-all { min-width: auto !important; padding: 0 4px !important; }
  .performance-summary { grid-template-columns: auto minmax(0, 1fr); gap: 14px; padding: 18px 16px; }
  .performance-score { width: 76px; height: 76px; border-width: 6px; }
  .performance-score strong { font-size: 21px; }
  .performance-summary-copy h3 { font-size: 15px; }
  .performance-metrics { grid-column: 1 / 3; grid-template-columns: repeat(2, 1fr); }
  .performance-lists { grid-template-columns: 1fr; }
  .performance-list { padding: 15px 14px; }
  .performance-list + .performance-list { border-top: 1px solid #e7ece8; border-left: 0; }
}
</style>
